// Optional Google Places enhancement. Native form fields remain usable without it.
export function mapPlaceAddress(components = []) {
  const part = (type, short = false) => {
    const c = components.find(c => c.types?.includes(type));
    return c?.[short ? 'shortText' : 'longText'] || '';
  };
  const code = part('country', true);
  const number = part('street_number');
  const route = part('route');
  const street = ['DE', 'AT', 'CH', 'NL', 'BE', 'ES', 'IT', 'PT', 'BR'].includes(code)
    ? [route, number] : [number, route];
  return {
    address: street.filter(Boolean).join(' ') || part('premise'),
    suite: part('subpremise'),
    city: part('locality') || part('postal_town') || part('sublocality_level_1') || part('administrative_area_level_2'),
    region: part('administrative_area_level_1', code === 'US' || code === 'CA'),
    postal: [part('postal_code'), part('postal_code_suffix')].filter(Boolean).join('-'),
    countryCode: code,
    countryName: part('country')
  };
}

export function findCountryOption(options, code, name) {
  if (!code) return '';
  const normalize = s => String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  const aliases = { US: ['United States', 'United States of America'], GB: ['United Kingdom'], HK: ['Hong Kong', 'Hong Kong SAR', 'Hong Kong SAR China'], MO: ['Macao', 'Macau', 'Macao SAR', 'Macao SAR China'], KR: ['South Korea'], KP: ['North Korea'], TW: ['Taiwan'], TR: ['Turkey', 'Türkiye'], CZ: ['Czech Republic', 'Czechia'], PS: ['Palestine', 'Palestinian Territories'], VA: ['Vatican City', 'Holy See (Vatican City State)'] };
  const names = [code, name, ...(aliases[code] || [])];
  if (typeof Intl.DisplayNames === 'function') {
    for (const locale of ['en', typeof document !== 'undefined' ? document.documentElement.lang || 'en' : 'en']) {
      try { names.push(new Intl.DisplayNames([locale], { type: 'region' }).of(code)); } catch { /* Manual selection remains available. */ }
    }
  }
  const candidates = new Set(names.filter(Boolean).map(normalize));
  return Array.from(options).find(o => o.value && (candidates.has(normalize(o.value)) || candidates.has(normalize(o.textContent))))?.value || '';
}

let placesLibrary;

function waitForMapsRuntime(timeout = 15000) {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const check = () => {
      if (window.google?.maps?.importLibrary) {
        resolve();
        return;
      }
      if (Date.now() - started >= timeout) {
        reject(new Error('Maps timed out'));
        return;
      }
      setTimeout(check, 50);
    };
    check();
  });
}

export function loadPlaces(key) {
  if (placesLibrary) return placesLibrary;
  placesLibrary = (async () => {
    if (!window.google?.maps?.importLibrary) {
      // Never load a second Maps runtime over another theme integration.
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existingScript) {
        // Other theme/app integrations can add Maps before this module runs.
        // Wait for that runtime instead of failing while its script is in flight.
        await waitForMapsRuntime();
      } else {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          let settled = false;
          const finish = callback => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            delete window.haroClinicMapsReady;
            callback();
          };
          const timer = setTimeout(() => finish(() => reject(new Error('Maps timed out'))), 15000);
          window.haroClinicMapsReady = () => finish(resolve);
          const params = new URLSearchParams({ key, v: 'weekly', loading: 'async', callback: 'haroClinicMapsReady' });
          script.src = 'https://maps.googleapis.com/maps/api/js?' + params;
          script.async = true;
          script.nonce = document.querySelector('script[nonce]')?.nonce || '';
          script.onerror = () => finish(() => reject(new Error('Maps unavailable')));
          document.head.append(script);
        });
        await waitForMapsRuntime(2000);
      }
    }
    return window.google.maps.importLibrary('places');
  })().catch(error => {
    // A later form open can retry after a temporary network or script failure.
    placesLibrary = undefined;
    throw error;
  });
  return placesLibrary;
}

if (typeof document !== 'undefined') {
  const dialog = document.getElementById('haro-clinic-sample');
  const host = dialog?.querySelector('[data-clinic-places]');
  const key = host?.dataset.apiKey?.trim();
  if (host && key && dialog.querySelector('#ClinicPracticeName')) {
    let started = false;
    let selection = 0;
    const filled = new Map();
    // An in-flight lookup must never overwrite details the applicant has just edited.
    dialog.addEventListener('input', event => {
      if (event.target.matches('#ClinicPracticeName, #ClinicPhone, #ClinicWebsite, #ClinicAddress, #ClinicSuite, #ClinicCity, #ClinicRegion, #ClinicPostal, #ClinicCountry')) {
        selection++;
        status.textContent = '';
      }
    });
    const status = host.querySelector('[data-places-status]');
    const setField = (id, value) => {
      const field = dialog.querySelector('#' + id);
      if (!field) return;
      // Keep a phone or website the applicant entered manually; replace prior lookup data.
      if (['ClinicPhone', 'ClinicWebsite'].includes(id) && field.value && field.value !== filled.get(id)) return;
      field.value = String(value || '').slice(0, field.maxLength > 0 ? field.maxLength : undefined);
      filled.set(id, field.value);
      field.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const initialize = async () => {
      if (started) return;
      started = true;
      host.hidden = false;
      status.textContent = (window.haroMessages?.loading_address_search_you_can_enter_your_deta_23418 || "Loading address search… You can enter your details below while it loads.");
      try {
        const { PlaceAutocompleteElement } = await loadPlaces(key);
        const widget = new PlaceAutocompleteElement();
        widget.placeholder = (window.haroMessages?.clinic_name_or_street_address_0b6ef || "Clinic name or street address");
        widget.setAttribute('aria-label', (window.haroMessages?.search_for_your_clinic_or_address_52087 || "Search for your clinic or address"));
        widget.setAttribute('aria-labelledby', 'ClinicLookupLabel');
        widget.setAttribute('aria-describedby', 'ClinicLookupHelp');
        widget.addEventListener('gmp-error', () => {
          status.textContent = (window.haroMessages?.address_search_is_unavailable_please_enter_you_b4491 || "Address search is unavailable. Please enter your details below.");
        });
        widget.addEventListener('gmp-select', async ({ placePrediction }) => {
          const current = ++selection;
          status.textContent = (window.haroMessages?.getting_address_details_adeb6 || "Getting address details…");
          try {
            const place = placePrediction.toPlace();
            // Use the prediction's name and retrieve the fields needed by the form.
            await place.fetchFields({ fields: ['addressComponents', 'types', 'internationalPhoneNumber', 'websiteURI'] });
            if (current !== selection) return;
            const credits = host.querySelector('[data-places-attributions]');
            credits.replaceChildren();
            for (const attribution of place.attributions || []) {
              const label = document.createElement('span');
              label.textContent = attribution.provider || '';
              if (attribution.providerURI?.startsWith('https://')) {
                const link = document.createElement('a');
                link.href = attribution.providerURI;
                link.textContent = label.textContent;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                credits.append(link, document.createTextNode(' '));
              } else credits.append(label, document.createTextNode(' '));
            }
            const address = mapPlaceAddress(place.addressComponents);
            let website = '';
            try {
              const parsed = new URL(place.websiteURI);
              if (['https:', 'http:'].includes(parsed.protocol)) website = parsed.href;
            } catch { /* Missing/invalid website stays manual. */ }
            const country = dialog.querySelector('#ClinicCountry');
            if (!address.countryCode) {
              status.textContent = (window.haroMessages?.this_result_has_no_complete_address_please_ent_1e5ca || "This result has no complete address. Please enter your delivery address below.");
              return;
            }
            if (place.types?.includes('establishment')) setField('ClinicPracticeName', placePrediction.mainText?.toString());
            setField('ClinicPhone', place.internationalPhoneNumber);
            setField('ClinicWebsite', website);
            setField('ClinicAddress', address.address);
            setField('ClinicSuite', address.suite);
            setField('ClinicCity', address.city);
            setField('ClinicRegion', address.region);
            setField('ClinicPostal', address.postal);
            setField('ClinicCountry', findCountryOption(country.options, address.countryCode, address.countryName));
            status.textContent = (window.haroMessages?.available_practice_details_added_please_review_73948 || "Available practice details added. Please review each field, add any suite or unit, and complete anything missing.");
          } catch {
            if (current === selection) status.textContent = (window.haroMessages?.we_could_not_retrieve_that_address_please_ente_ed9b7 || "We could not retrieve that address. Please enter your details below.");
          }
        });
        host.querySelector('[data-places-widget]').append(widget);
        status.textContent = '';
      } catch {
        started = false;
        status.textContent = (window.haroMessages?.address_search_is_unavailable_please_enter_you_b4491 || "Address search is unavailable. Please enter your details below.");
      }
    };
    dialog.addEventListener('haro:clinic-open', initialize);
    if (dialog.open) initialize();
  }
}
